import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, MapPin, Truck, CreditCard, Banknote, ShieldCheck, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";

const STEPS = ["Address", "Delivery", "Summary", "Payment"];
const EMPTY_ADDR = { full_name: "", mobile: "", house: "", street: "", area: "", city: "", state: "", pincode: "", landmark: "", is_default: false };

const PAYMENTS = [
  { id: "razorpay", label: "Online Payment (Razorpay)", desc: "UPI, Credit/Debit Cards & Net Banking — secure checkout", icon: CreditCard },
  { id: "cod", label: "Cash on Delivery", desc: "Pay when your gift arrives", icon: Banknote },
];

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load the payment gateway. Please check your connection."));
    document.body.appendChild(s);
  });

export function AddressForm({ initial = EMPTY_ADDR, onSave, onCancel, testPrefix = "addr" }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const fields = [
    ["full_name", "Full Name", "text"], ["mobile", "Mobile Number", "tel"], ["house", "House / Flat No.", "text"],
    ["street", "Street", "text"], ["area", "Area / Locality", "text"], ["city", "City", "text"],
    ["state", "State", "text"], ["pincode", "Pincode", "text"], ["landmark", "Landmark (optional)", "text"],
  ];

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testPrefix}-form`}>
      {fields.map(([k, label, type]) => (
        <input key={k} required={k !== "landmark"} type={type} value={form[k]} onChange={set(k)} placeholder={label}
          className="h-11 px-4 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309]" data-testid={`${testPrefix}-${k}`} />
      ))}
      <label className="flex items-center gap-2 text-sm text-stone-600 sm:col-span-2">
        <input type="checkbox" checked={form.is_default} onChange={set("is_default")} className="accent-[#B45309]" data-testid={`${testPrefix}-default`} /> Set as default address
      </label>
      <div className="flex gap-3 sm:col-span-2">
        <button disabled={busy} className="h-11 px-6 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid={`${testPrefix}-save`}>{busy ? "Saving…" : "Save Address"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="h-11 px-6 rounded-full border border-stone-300 text-sm font-semibold text-stone-600" data-testid={`${testPrefix}-cancel`}>Cancel</button>}
      </div>
    </form>
  );
}

export default function Checkout() {
  const { user } = useAuth();
  const { cart, fetchCart } = useShop();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState(null);
  const [addingAddr, setAddingAddr] = useState(false);
  const [deliveryType, setDeliveryType] = useState("standard");
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [payment, setPayment] = useState("cod");
  const [payConfig, setPayConfig] = useState(null);
  const [giftNote, setGiftNote] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (user === false) nav("/auth", { state: { from: "/checkout" } });
    if (user) {
      fetchCart();
      api.get("/addresses").then(({ data }) => {
        setAddresses(data.items);
        const def = data.items.find((a) => a.is_default) || data.items[0];
        if (def) setAddressId(def.id);
      }).catch(() => {});
      api.get("/payments/config").then(({ data }) => setPayConfig(data)).catch(() => setPayConfig({ razorpay_enabled: false }));
    }
  }, [user, nav, fetchCart]);

  const address = useMemo(() => addresses.find((a) => a.id === addressId), [addresses, addressId]);

  useEffect(() => {
    if (address?.pincode) {
      api.get("/delivery/estimate", { params: { pincode: address.pincode } }).then(({ data }) => {
        setDeliveryInfo(data);
        if (!data.same_day && deliveryType === "same_day") setDeliveryType("standard");
      }).catch(() => setDeliveryInfo(null));
    }
  }, [address?.pincode]); // eslint-disable-line react-hooks/exhaustive-deps

  const deliveryCharge = deliveryType === "same_day" ? 149 : deliveryType === "express" ? 99 : cart.subtotal >= 999 ? 0 : 79;
  const total = Math.max(cart.subtotal + deliveryCharge, 0);

  const saveAddress = async (form) => {
    await api.post("/addresses", form);
    const { data } = await api.get("/addresses");
    setAddresses(data.items);
    setAddressId(data.items[data.items.length - 1].id);
    setAddingAddr(false);
    toast.success("Address saved");
  };

  const runRazorpay = async () => {
    // Creates a Razorpay test order, opens the Razorpay checkout, verifies the signature server-side.
    const { data: rOrder } = await api.post("/payments/razorpay/order", { amount: Math.round(total * 100) });
    await loadRazorpayScript();
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: payConfig.key_id,
        amount: rOrder.amount,
        currency: "INR",
        order_id: rOrder.id,
        name: "Customize Gift Online",
        description: "Gift order payment",
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#B45309" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", resp);
            resolve(resp);
          } catch (e) {
            reject(e);
          }
        },
        modal: { ondismiss: () => reject(new Error("Payment was cancelled. Your order has not been placed — you can try again anytime.")) },
      });
      rzp.on("payment.failed", () => reject(new Error("Payment failed. Your order has not been placed — please try again or choose Cash on Delivery.")));
      rzp.open();
    });
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      let rpResp = null;
      if (payment === "razorpay") {
        if (!payConfig?.razorpay_enabled) {
          toast.error("Online payments are being set up. Please choose Cash on Delivery for now.");
          setPlacing(false);
          return;
        }
        rpResp = await runRazorpay();
      }
      const { data: order } = await api.post("/checkout", {
        address_id: addressId, delivery_type: deliveryType, payment_method: payment,
        coupon_code: cart.coupon_code || undefined, gift_note: giftNote,
        razorpay_payment_id: rpResp?.razorpay_payment_id || undefined,
        razorpay_order_id: rpResp?.razorpay_order_id || undefined,
        razorpay_signature: rpResp?.razorpay_signature || undefined,
      });
      await fetchCart();
      nav(`/order-success/${order.order_id}`, { state: { order } });
    } catch (err) {
      toast.error(errMsg(err, "We couldn't complete your payment. Your order has not been placed. Please try another payment method."));
    } finally {
      setPlacing(false);
    }
  };

  if (!user) return null;

  if (!cart.items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-stone-900">Your cart is empty</h1>
        <button onClick={() => nav("/shop")} className="mt-8 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold" data-testid="checkout-empty-shop">Explore Gifts</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 lg:py-14" data-testid="checkout-page">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">Checkout</h1>

      <div className="flex items-center gap-2 mt-8 mb-10" data-testid="checkout-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0 transition-colors ${i < step ? "bg-[#78866B] text-white" : i === step ? "bg-[#B45309] text-white" : "bg-[#F5EFE6] text-stone-400"}`}>
              {i < step ? <Check size={14} /> : i + 1}
            </span>
            <span className={`text-xs font-semibold hidden sm:block ${i === step ? "text-stone-900" : "text-stone-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-stone-200" />}
          </div>
        ))}
      </div>

      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}>
        {step === 0 && (
          <div data-testid="step-address">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-5">Where should the magic arrive?</h2>
            <div className="space-y-3">
              {addresses.map((a) => (
                <label key={a.id} className={`flex gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-colors ${addressId === a.id ? "border-[#B45309] bg-[#F9F3EA]" : "border-stone-200 bg-white hover:border-[#E0A96D]"}`} data-testid={`address-option-${a.id}`}>
                  <input type="radio" name="addr" checked={addressId === a.id} onChange={() => setAddressId(a.id)} className="accent-[#B45309] mt-1" />
                  <div>
                    <p className="font-bold text-stone-800 text-sm">{a.full_name} <span className="font-normal text-stone-500">· {a.mobile}</span></p>
                    <p className="text-sm text-stone-500 mt-1">{a.house}, {a.street}, {a.area}, {a.city}, {a.state} — {a.pincode}</p>
                    {a.landmark && <p className="text-xs text-stone-400 mt-0.5">Landmark: {a.landmark}</p>}
                  </div>
                </label>
              ))}
            </div>
            {addingAddr ? (
              <div className="mt-5 p-5 rounded-2xl bg-white border border-[#E9E2D7]"><AddressForm onSave={saveAddress} onCancel={() => setAddingAddr(false)} /></div>
            ) : (
              <button onClick={() => setAddingAddr(true)} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#B45309] hover:underline" data-testid="add-address-btn"><Plus size={15} /> Add a new address</button>
            )}
            <StepNav onNext={() => (addressId ? setStep(1) : toast.error("Please select or add a delivery address"))} nextLabel="Continue to Delivery" />
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-delivery">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-5">How fast should it reach?</h2>
            <div className="space-y-3">
              {(deliveryInfo?.options || [{ type: "standard", label: "Standard Delivery", eta: "3–5 days", note: "Free above ₹999" }]).map((o) => {
                const charge = o.type === "same_day" ? 149 : o.type === "express" ? 99 : cart.subtotal >= 999 ? 0 : 79;
                return (
                  <label key={o.type} className={`flex items-center gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-colors ${deliveryType === o.type ? "border-[#B45309] bg-[#F9F3EA]" : "border-stone-200 bg-white hover:border-[#E0A96D]"}`} data-testid={`delivery-${o.type}`}>
                    <input type="radio" name="delivery" checked={deliveryType === o.type} onChange={() => setDeliveryType(o.type)} className="accent-[#B45309]" />
                    <Truck size={18} className="text-[#B45309]" />
                    <div className="flex-1">
                      <p className="font-bold text-stone-800 text-sm">{o.label}</p>
                      <p className="text-xs text-stone-500">Get it by {o.eta}</p>
                    </div>
                    <span className="text-sm font-bold text-stone-800">{charge === 0 ? "FREE" : inr(charge)}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-5">
              <label className="text-sm font-bold text-stone-800">Gift note (optional)</label>
              <textarea value={giftNote} onChange={(e) => setGiftNote(e.target.value)} rows={2} placeholder="A few words we'll hand-write on the card…" className="w-full mt-2 px-4 py-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] resize-none" data-testid="gift-note-input" />
            </div>
            <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} nextLabel="Review Order" />
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-summary">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-5">Everything looks right?</h2>
            <div className="bg-white rounded-2xl border border-[#E9E2D7] divide-y divide-stone-100">
              {cart.items.map((i, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4">
                  {i.image && <img src={i.image} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-800 line-clamp-1">{i.name}</p>
                    <p className="text-xs text-stone-400">Qty {i.qty}{i.personalization?.recipient_name ? ` · For ${i.personalization.recipient_name}` : ""}</p>
                    {i.personalization?.photo_data && <img src={i.personalization.photo_data} alt="Personalization upload" className="w-9 h-9 rounded-lg object-cover border border-stone-200 mt-1" />}
                  </div>
                  <span className="text-sm font-bold text-stone-900">{inr(i.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#F9F3EA] rounded-2xl p-5 mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
              {cart.coupon_code && <div className="flex justify-between text-[#5c6b52] font-semibold"><span>Coupon ({cart.coupon_code})</span><span>applied at order</span></div>}
              <div className="flex justify-between text-stone-600"><span>Delivery ({deliveryType.replace("_", " ")})</span><span>{deliveryCharge === 0 ? "FREE" : inr(deliveryCharge)}</span></div>
              <div className="border-t border-[#E9E2D7] pt-2 flex justify-between font-bold text-stone-900 text-base"><span>To pay</span><span data-testid="summary-total">{inr(total)}</span></div>
            </div>
            {address && <p className="text-xs text-stone-500 mt-3 flex items-center gap-1.5"><MapPin size={12} /> Delivering to {address.full_name}, {address.city} — {address.pincode}</p>}
            <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)} nextLabel="Proceed to Payment" />
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-payment">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-2">How would you like to pay?</h2>
            <p className="flex items-center gap-2 text-xs text-stone-500 mb-5"><ShieldCheck size={13} className="text-[#78866B]" /> {payConfig?.razorpay_enabled && payConfig?.mode === "live" ? "Secure online payment via Razorpay. Amount payable:" : "Test mode — no real money moves. Amount payable:"} <b>{inr(total)}</b></p>
            <div className="space-y-3">
              {PAYMENTS.map((p) => (
                <label key={p.id} className={`flex items-center gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-colors ${payment === p.id ? "border-[#B45309] bg-[#F9F3EA]" : "border-stone-200 bg-white hover:border-[#E0A96D]"}`} data-testid={`payment-${p.id}`}>
                  <input type="radio" name="payment" checked={payment === p.id} onChange={() => setPayment(p.id)} className="accent-[#B45309]" />
                  <p.icon size={18} className="text-[#B45309]" />
                  <div className="flex-1">
                    <p className="font-bold text-stone-800 text-sm">{p.label}</p>
                    <p className="text-xs text-stone-500">{p.desc}</p>
                  </div>
                  {p.id === "razorpay" && payConfig && !payConfig.razorpay_enabled && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-amber-50 text-amber-700" data-testid="razorpay-pending-badge">Being set up</span>
                  )}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-8">
              <button onClick={() => setStep(2)} className="h-12 px-6 rounded-full border border-stone-300 text-sm font-semibold text-stone-600" data-testid="step-back-3">Back</button>
              <button onClick={placeOrder} disabled={placing} className="flex-1 h-12 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid="place-order-btn">
                {placing ? "Placing your order…" : `Place Order · ${inr(total)}`}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function StepNav({ onBack, onNext, nextLabel }) {
  return (
    <div className="flex items-center gap-3 mt-8">
      {onBack && <button onClick={onBack} className="h-12 px-6 rounded-full border border-stone-300 text-sm font-semibold text-stone-600" data-testid="step-back">Back</button>}
      <button onClick={onNext} className="flex-1 h-12 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-[#B45309] transition-colors flex items-center justify-center gap-2" data-testid="step-next">
        {nextLabel} <ChevronRight size={15} />
      </button>
    </div>
  );
}
